import { useEffect, useState, type ReactNode } from 'react';
import { MdClose } from 'react-icons/md';

export interface ConfirmModalProps {
    open: boolean;
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    /** When set, the confirm button stays disabled until a reason is typed. */
    requireReason?: boolean;
    reasonLabel?: string;
    busy?: boolean;
    extra?: ReactNode;
    onConfirm: (reason: string) => void;
    onClose: () => void;
}

/** Confirmation dialog for privileged actions that must carry a reason (FR-37). */
export default function ConfirmModal({
    open,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    requireReason = false,
    reasonLabel = 'Reason',
    busy = false,
    extra,
    onConfirm,
    onClose,
}: ConfirmModalProps) {
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const canConfirm = !busy && (!requireReason || reason.trim().length >= 3);

    return (
        <div className="fixed inset-0 z-[900] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="animate-fade-in w-full max-w-md rounded-t-2xl bg-white p-6 sm:rounded-2xl"
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 className="text-[18px] font-bold text-secondary">{title}</h2>
                    <button
                        type="button"
                        title="Close"
                        onClick={onClose}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#888] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdClose size={18} />
                    </button>
                </div>

                {description && <p className="mt-2 text-[14px] leading-6 text-[#747474]">{description}</p>}

                {extra && <div className="mt-4">{extra}</div>}

                {requireReason && (
                    <div className="mt-4">
                        <label htmlFor="confirm-reason" className="block text-[13px] font-semibold text-secondary">
                            {reasonLabel} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="confirm-reason"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="This is recorded in the audit log"
                            className="mt-1.5 w-full rounded-lg border border-[#D2D2D2] bg-gray-50 px-4 py-3 text-[14px] outline-none transition-colors focus:border-[#151515] focus:bg-white focus:ring-2 focus:ring-[#151515]"
                        />
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-[#E0E0E0] px-4 py-2.5 text-[14px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        disabled={!canConfirm}
                        onClick={() => onConfirm(reason.trim())}
                        className={`rounded-lg px-4 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${
                            destructive ? 'bg-red-600' : 'bg-secondary'
                        }`}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
